import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import {
  Container, Typography, Box, TextField, Button, Grid, Paper, CircularProgress,
  Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Tabs, Tab,
  Card, CardContent, CardActions, CardMedia, Chip, MenuItem, Select, FormControl,
  InputLabel, Autocomplete, Divider, Avatar, Tooltip, FormHelperText, useTheme
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  PhotoCamera as PhotoCameraIcon,
  LocalShipping as LocalShippingIcon,
  Send as SendIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  ImageNotSupported as ImageNotSupportedIcon,
  HourglassEmpty as HourglassEmptyIcon,
  Sync as SyncIcon,
  CheckCircleOutline as CheckCircleOutlineIcon
} from '@mui/icons-material';
import { AuthContext } from '../../contexts/AuthContext';
import { useSnackbar } from 'notistack';
import { 
  collection, 
  getDocs, 
  query, 
  where, 
  doc, 
  onSnapshot, 
  updateDoc, 
  getDoc, 
  addDoc, 
  orderBy, 
  limit,
  serverTimestamp,
  Timestamp,
  deleteDoc
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage, functions } from '../../firebase';
import { httpsCallable } from 'firebase/functions';
import { format } from 'date-fns';
import { pl } from 'date-fns/locale';

// --- STAŁE --- 
const LOCATIONS = [
  { shortName: 'PAN', name: 'Panorama M&M GOLD' },
  { shortName: 'VVS', name: 'Panorama VVS' },
  { shortName: 'MODO', name: 'Modo M&M GOLD' },
  { shortName: 'KLIF', name: 'Klif M&M GOLD' },
  { shortName: 'GP', name: 'Galeria Północna M&M GOLD' },
  { shortName: 'SB', name: 'Stary Browar M&M GOLD' },
  { shortName: 'MB', name: 'Marbella' },
  { shortName: 'GPAN', name: 'Główny Panorama', adminOnly: true },
  { shortName: 'GMODO', name: 'Główny Modo', adminOnly: true }
];

const TRANSFER_STATUS_CONFIG = {
  pending: { label: 'Oczekujący', color: 'warning', icon: <LocalShippingIcon /> },
  shipped: { label: 'Wysłany', color: 'info', icon: <SendIcon /> },
  completed: { label: 'Zrealizowany', color: 'success', icon: <CheckCircleIcon /> },
  cancelled: { label: 'Anulowany', color: 'error', icon: <CancelIcon /> },
  locally_confirmed: { label: 'Potwierdzony lokalnie', color: 'secondary', icon: <CheckCircleIcon /> }
};

// Nowe metody wysyłki
const SHIPPING_METHODS = [
  { value: 'pan_wojtek', label: 'Pan Wojtek (transport wewnętrzny)' },
  { value: 'courier', label: 'Kurier' },
  { value: 'personal', label: 'Osobiście' },
  { value: 'internal', label: 'Transfer wewnętrzny' },
  { value: 'other', label: 'Inny (opisz w uwagach)' }
];

// Funkcja pomocnicza do formatowania dat w formacie wymaganym przez input type="date"
const formatDateForInput = (date) => {
  try {
    if (!date) return '';
    
    if (typeof date === 'string') {
      // Jeśli to już jest string w formacie YYYY-MM-DD, zwróć go
      if (/^\d{4}-\d{2}-\d{2}$/.test(date)) return date;
      // W przeciwnym razie spróbuj przekonwertować string na datę
      date = new Date(date);
    }
    
    if (date instanceof Date) {
      // Format YYYY-MM-DD wymagany przez input type="date"
      return format(date, 'yyyy-MM-dd');
    }
    return '';
  } catch (error) {
    console.error('Błąd formatowania daty dla input:', error);
    return '';
  }
};

// Mapowanie starych wartości do nowych
const mapLegacyShippingMethod = (oldValue) => {
  const mappings = {
    'courier_wojtek': 'pan_wojtek'
  };
  return mappings[oldValue] || oldValue;
};

// --- KOMPONENT --- 
const ProductTransfers = () => {
  const { currentUser, isAdmin } = useContext(AuthContext);
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();

  // Stany dla danych
  const [transfers, setTransfers] = useState([]);
  const [filteredTransfers, setFilteredTransfers] = useState([]);
  const [employees, setEmployees] = useState([]);
  
  // Stany UI
  const [activeTab, setActiveTab] = useState(0); // 0: Wszystkie/Moje, 1: Wysłane, 2: Otrzymane
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Stany dialogów
  const [openTransferDialog, setOpenTransferDialog] = useState(false);
  const [openProductSearchDialog, setOpenProductSearchDialog] = useState(false);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [productSearchResults, setProductSearchResults] = useState([]);
  const [productSearchLoading, setProductSearchLoading] = useState(false);

  const [openImagePreviewDialog, setOpenImagePreviewDialog] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState('');

  // Stany wyszukiwania produktów
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);

  // Stan wybranego produktu
  const [selectedProduct, setSelectedProduct] = useState(null);
  
  // Funkcja pomocnicza do formatowania daty do formatu YYYY-MM-DD
  const formatDateForInput = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Miesiące są 0-indexed
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  // Stan formularza transferu
  const initialFormData = {
    product_id: '',
    product_name: '',
    product_sku: '',
    product_collection: '',
    product_image_url: null,
    current_location: '',
    origin_location: '',
    destination_location: 'PAN',  // Domyślna wartość dla lokalizacji docelowej
    sender_id: currentUser?.uid || '',
    recipient_name: '',
    recipient_id: '',
    shipping_method: 'pan_wojtek',
    status: 'pending',
    notes: '',
    transfer_image_url: null,
    estimated_arrival: formatDateForInput(new Date(Date.now() + 24 * 60 * 60 * 1000)) // Domyślnie +1 dzień
  };
  const [formData, setFormData] = useState(initialFormData);
  const [editingTransfer, setEditingTransfer] = useState(null); // Przechowuje dane edytowanego transferu

  // --- LOGIKA POBIERANIA DANYCH ---
  const fetchEmployees = useCallback(async () => {
    // Sprawdź, czy użytkownik jest zalogowany
    if (!currentUser || !currentUser.uid) {
      console.log('Pomijam fetchEmployees - brak zalogowanego użytkownika');
      return;
    }
    
    console.log('Rozpoczynam pobieranie listy pracowników...');
    setLoading(true);
    try {
      const usersCollection = collection(db, 'users');
      const usersSnapshot = await getDocs(usersCollection);
      const employeesList = usersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        fullName: `${doc.data().firstName || ''} ${doc.data().lastName || ''}`.trim() || doc.data().email
      }));
      
      console.log(`Pobrano ${employeesList.length} pracowników:`, employeesList);
      setEmployees(employeesList);
    } catch (err) {
      console.error('Błąd podczas pobierania pracowników:', err);
      enqueueSnackbar('Nie udało się pobrać listy pracowników.', { variant: 'error' });
      setError('Nie udało się pobrać listy pracowników.');
    } finally {
      setLoading(false); // Zakończ ładowanie po pracownikach, fetchTransfers ma swoje
    }
  }, [enqueueSnackbar]);

  const fetchTransfers = useCallback(async () => {
    if (!currentUser || !currentUser.uid) {
      console.log('Pomijam fetchTransfers - brak zalogowanego użytkownika');
      return;
    }
    
    setLoading(true);
    setError(null);
    try {
      const transfersCollectionRef = collection(db, 'transfers');
      let q;

      if (isAdmin) {
        // Admin widzi wszystkie transfery
        q = query(transfersCollectionRef, orderBy('created_at', 'desc'));
      } else {
        // Zwykły użytkownik widzi transfery, gdzie jest nadawcą LUB odbiorcą
        // Musimy zrobić dwa osobne zapytania i połączyć wyniki
        const senderQuery = query(transfersCollectionRef, where('sender_id', '==', currentUser.uid));
        const recipientQuery = query(transfersCollectionRef, where('recipient_id', '==', currentUser.uid));

        const [senderSnapshot, recipientSnapshot] = await Promise.all([
          getDocs(senderQuery),
          getDocs(recipientQuery)
        ]);

        const userTransfers = {};
        senderSnapshot.forEach(doc => {
          userTransfers[doc.id] = { id: doc.id, ...doc.data() };
        });
        recipientSnapshot.forEach(doc => {
          // Dodaj tylko jeśli nie istnieje, aby uniknąć duplikatów (gdy użytkownik jest i nadawcą i odbiorcą)
          if (!userTransfers[doc.id]) {
            userTransfers[doc.id] = { id: doc.id, ...doc.data() };
          }
        });
        
        const transfersList = Object.values(userTransfers).sort((a, b) => {
          const dateA = a.created_at instanceof Timestamp ? a.created_at.toDate() : new Date(0);
          const dateB = b.created_at instanceof Timestamp ? b.created_at.toDate() : new Date(0);
          return dateB - dateA;
        });

        setTransfers(transfersList);
        // Filtrowanie zostanie wykonane w osobnym useEffect
        setLoading(false);
        return; // Zakończ wcześniej dla nie-admina
      }

      // Dla admina kontynuuj z pojedynczym zapytaniem
      const querySnapshot = await getDocs(q);
      const transfersList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      setTransfers(transfersList);
    } catch (err) {
      console.error('Błąd podczas pobierania transferów:', err);
      enqueueSnackbar('Nie udało się pobrać transferów.', { variant: 'error' });
      setError('Nie udało się pobrać transferów.');
    } finally {
      setLoading(false);
    }
  }, [currentUser, isAdmin, enqueueSnackbar]);

  useEffect(() => {
    // Sprawdź, czy currentUser jest w pełni zainicjalizowany i ma token
    if (currentUser && currentUser.uid) {
      // Małe opóźnienie, aby mieć pewność, że token autoryzacyjny jest prawidłowo załadowany
      const timeout = setTimeout(() => {
        fetchTransfers();
        fetchEmployees();
      }, 300); // 300ms powinno wystarczyć na pełną inicjalizację tokenów
      
      return () => clearTimeout(timeout);
    }
  }, [currentUser, fetchTransfers, fetchEmployees]);

  // Po skasowaniu kodu lokalnych potwierdzeń transferów

  // --- LOGIKA FILTROWANIA ZAKŁADEK ---
  useEffect(() => {
    if (!currentUser) {
      setFilteredTransfers([]);
      return;
    }
    if (activeTab === 0) { // Wszystkie (dla admina) lub Moje (dla usera)
      setFilteredTransfers(transfers);
    } else if (activeTab === 1) { // Wysłane przeze mnie
      setFilteredTransfers(transfers.filter(t => t.sender_id === currentUser.uid));
    } else if (activeTab === 2) { // Odebrane przeze mnie
      setFilteredTransfers(transfers.filter(t => t.recipient_id === currentUser.uid));
    }
  }, [activeTab, transfers, currentUser]);

  // --- OBSŁUGA ZDARZEŃ (PLACEHOLDERS) ---
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleOpenImagePreview = (url) => {
    if (url) {
      setPreviewImageUrl(url);
      setOpenImagePreviewDialog(true);
    } else {
      enqueueSnackbar('Brak zdjęcia do wyświetlenia.', { variant: 'info' });
    }
  };
  
  // Funkcja do szybkiego wyboru szacowanego czasu dostawy
  const handleQuickDateSelect = (days) => {
    const today = new Date();
    const targetDate = new Date(today);
    targetDate.setDate(today.getDate() + days);
    
    setFormData(prev => ({
      ...prev,
      estimated_arrival: formatDateForInput(targetDate)
    }));
  };

  const handleOpenNewTransferDialog = () => {
    setEditingTransfer(null);
    // Ustawienie domyślnej lokalizacji źródłowej, jeśli użytkownik ją ma
    const defaultFromLocation = currentUser?.defaultLocation || '';
    setFormData({ ...initialFormData, origin_location: defaultFromLocation });
    setOpenTransferDialog(true);
  };

  const handleOpenEditTransferDialog = (transfer) => {
    setEditingTransfer(transfer);
    setFormData({
      product_id: transfer.product_id || '',
      product_name: transfer.product_name || '',
      product_sku: transfer.product_sku || '',
      product_image_url: transfer.product_image_url || '',
      from_location: transfer.from_location || '',
      to_location: transfer.to_location || '',
      recipient_id: transfer.recipient_id || '',
      recipient_name: transfer.recipient_name || '',
      shipping_method: transfer.shipping_method || 'courier_wojtek',
      notes: transfer.notes || '',
      status: transfer.status || 'pending',
      transfer_image_file: null, // Resetuj plik przy edycji
      transfer_image_display_url: transfer.transfer_image_url || '' // Użyj istniejącego URL zdjęcia transferu
    });
    setOpenTransferDialog(true);
  };
  
  const handleCloseTransferDialog = () => {
    setOpenTransferDialog(false);
    setEditingTransfer(null);
    setFormData(initialFormData);
    setPreviewImageUrl(''); // Reset preview for transfer form image
    setProductSearchTerm('');
    setProductSearchResults([]);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Jeśli zmieniamy recipient_id, aktualizujemy również recipient_name
    if (name === 'recipient_id') {
      const selectedEmployee = employees.find(emp => emp.id === value);
      const recipientName = selectedEmployee ? 
        (selectedEmployee.fullName || selectedEmployee.email || selectedEmployee.id) : '';
      
      setFormData(prev => ({ 
        ...prev, 
        [name]: value,
        recipient_name: recipientName
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleAutocompleteChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setFormData(prev => ({ 
        ...prev, 
        transfer_image_file: file,
        transfer_image_display_url: URL.createObjectURL(file) // For local preview
      }));
    }
  };

  const handleSaveTransfer = async () => {
    // Walidacja
    if (!formData.product_id || !formData.product_name) {
      enqueueSnackbar('ID Produktu i Nazwa Produktu są wymagane.', { variant: 'warning' });
      return;
    }
    if (!formData.origin_location || !formData.destination_location) {
      enqueueSnackbar('Lokalizacja źródłowa i docelowa są wymagane.', { variant: 'warning' });
      return;
    }
    if (formData.origin_location === formData.destination_location) {
      enqueueSnackbar('Lokalizacja źródłowa i docelowa nie mogą być takie same.', { variant: 'warning' });
      return;
    }
    if (!formData.recipient_id) {
      enqueueSnackbar('Odbiorca jest wymagany.', { variant: 'warning' });
      return;
    }

    setLoading(true);
    try {
      let imageUrl = editingTransfer?.transfer_image_url || formData.product_image_url || ''; // Domyślnie istniejący URL produktu lub transferu
      if (formData.transfer_image_file) {
        const imageRef = ref(storage, `transfer_images/${currentUser.uid}_${Date.now()}_${formData.transfer_image_file.name}`);
        await uploadBytes(imageRef, formData.transfer_image_file);
        imageUrl = await getDownloadURL(imageRef);
      }

      const transferData = {
        product_id: formData.product_id,
        product_name: formData.product_name,
        product_sku: formData.product_sku,
        product_image_url: formData.product_image_url,
        product_collection: formData.product_collection || 'products',
        origin_location: formData.origin_location,
        destination_location: formData.destination_location,
        recipient_id: formData.recipient_id,
        recipient_name: formData.recipient_name, // Assumes recipient_name is correctly set in formData upon selection
        shipping_method: formData.shipping_method,
        notes: formData.notes,
        status: formData.status,
        transfer_image_url: imageUrl, // This is the URL from storage or existing URL
        sender_id: currentUser.uid,
        sender_name: `${currentUser.firstName} ${currentUser.lastName}`.trim() || currentUser.email, // Robust sender name
        created_at: editingTransfer ? editingTransfer.created_at : serverTimestamp(), // Correct handling for new vs. edit
        updated_at: serverTimestamp(),
        estimated_arrival: formData.estimated_arrival ? formData.estimated_arrival : formatDateForInput(new Date(Date.now() + 24 * 60 * 60 * 1000)),
      };
      
      // Usuwamy pola które nie są częścią modelu danych transferu w Firestore
      delete transferData.transfer_image_file;
      delete transferData.transfer_image_display_url;
      if (!editingTransfer) {
         delete transferData.product_image_url; // To jest tylko do wyświetlania w formularzu
      }

      if (editingTransfer) {
        const transferDocRef = doc(db, 'transfers', editingTransfer.id);
        await updateDoc(transferDocRef, transferData);
        enqueueSnackbar('Transfer został pomyślnie zaktualizowany!', { variant: 'success' });
      } else {
        await addDoc(collection(db, 'transfers'), transferData);
        enqueueSnackbar('Nowy transfer został pomyślnie utworzony!', { variant: 'success' });
      }
      
      handleCloseTransferDialog();
      fetchTransfers();

    } catch (err) {
      console.error('Błąd podczas zapisywania transferu:', err);
      enqueueSnackbar(`Błąd: ${err.message}`, { variant: 'error' });
      setError('Nie udało się zapisać transferu.');
    } finally {
      setLoading(false);
    }
  };

  // Funkcja do usuwania transferu (tylko dla adminów)
  const handleDeleteTransfer = async (transferId) => {
    if (!isAdmin) {
      enqueueSnackbar('Tylko administratorzy mogą usuwać transfery.', { variant: 'error' });
      return;
    }

    try {
      await deleteDoc(doc(db, 'transfers', transferId));
      enqueueSnackbar('Transfer został usunięty!', { variant: 'success' });
      fetchTransfers();
    } catch (err) {
      console.error('Błąd podczas usuwania transferu:', err);
      enqueueSnackbar('Nie udało się usunąć transferu.', { variant: 'error' });
    }
  };

  // Funkcja do potwierdzania odbioru przesyłki (tylko dla odbiorcy)
  const handleConfirmReceipt = async (transfer) => {
    if (transfer.recipient_id !== currentUser?.uid) {
      enqueueSnackbar('Tylko odbiorca może potwierdzić odbiór przesyłki.', { variant: 'error' });
      return;
    }

    try {
      setLoading(true);
      
      // 1. Najpierw aktualizujemy dokument transferu w Firestore
      const transferRef = doc(db, 'transfers', transfer.id);
      await updateDoc(transferRef, {
        status: 'completed',
        confirmation_date: serverTimestamp(),
        confirmed_by: currentUser.uid,
        confirmed_by_name: currentUser.displayName || 'Użytkownik'
      });
      
      // 2. Aktualizujemy lokalizację produktu w Firestore
      const productRef = doc(db, transfer.product_collection, transfer.product_id);
      await updateDoc(productRef, {
        location: transfer.destination_location,
        last_updated: serverTimestamp()
      });
      
      // 3. Aktualizujemy lokalny stan transferów dla natychmiastowej reakcji UI
      const updatedTransfers = transfers.map(t => {
        if (t.id === transfer.id) {
          return {
            ...t, 
            status: 'completed', 
            confirmation_date: new Date().toISOString()
          };
        }
        return t;
      });
      
      setTransfers(updatedTransfers);
      
      // 4. Komunikat o sukcesie
      enqueueSnackbar('Transfer został potwierdzony. Lokalizacja produktu została zaktualizowana.', 
        { variant: 'success', autoHideDuration: 4000 });
      
      // 5. Odświeżamy listę transferów po krótkiej chwili
      setTimeout(() => {
        fetchTransfers();
      }, 1000);
      
    } catch (err) {
      console.error('Błąd podczas potwierdzania transferu:', err);
      
      if (err.message && err.message.includes('permission')) {
        // Jeśli błąd dotyczy uprawnień, daj jasny komunikat
        enqueueSnackbar('Brak uprawnień do aktualizacji transferu. Skontaktuj się z administratorem.', 
          { variant: 'warning', autoHideDuration: 6000 });
      } else {
        // Inne błędy
        enqueueSnackbar(`Wystąpił błąd: ${err.message}`, { variant: 'error' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleProductSearch = async (searchTerm) => {
    if (!searchTerm || searchTerm.trim().length < 2) {
      setProductSearchResults([]);
      return;
    }
    
    console.log('Szukanie produktu:', searchTerm);
    setProductSearchLoading(true);
    
    try {
      const productsRef = collection(db, 'products');
      const watchesRef = collection(db, 'watches');
      const jewelryRef = collection(db, 'jewelry');
      
      const searchTermLower = searchTerm.toLowerCase().trim();
      const allResults = [];
      
      // Uproszczona funkcja do pobierania i filtrowania produktów bezpośrednio
      const searchInCollection = async (collectionRef, collectionType) => {
        try {
          // Pobierz więcej dokumentów do filtrowania
          const snapshot = await getDocs(query(collectionRef, limit(100)));
          const matchingDocs = [];
          
          snapshot.forEach(doc => {
            const data = doc.data();
            const id = doc.id;
            let matches = false;
            
            // Sprawdź wszystkie pola, które mogą zawierać szukany tekst
            const fieldsToCheck = ['name', 'brand', 'model', 'sku', 'jewelry_name'];
            
            for (const field of fieldsToCheck) {
              const fieldValue = data[field];
              if (fieldValue && typeof fieldValue === 'string') {
                const fieldValueLower = fieldValue.toLowerCase();
                if (fieldValueLower.includes(searchTermLower)) {
                  matches = true;
                  break; // Wystarczy jedno dopasowanie
                }
              }
            }
            
            // Sprawdź również ID dokumentu
            if (id.toLowerCase().includes(searchTermLower)) {
              matches = true;
            }
            
            if (matches) {
              matchingDocs.push({ 
                id, 
                _collectionType: collectionType, 
                ...data 
              });
            }
          });
          
          console.log(`Znaleziono ${matchingDocs.length} dopasowanych produktów w kolekcji ${collectionType}`);
          return matchingDocs;
        } catch (error) {
          console.error(`Błąd podczas wyszukiwania w kolekcji ${collectionType}:`, error);
          return [];
        }
      };
      
      // Wykonaj wyszukiwanie równolegle w trzech kolekcjach
      const [productsResults, watchesResults, jewelryResults] = await Promise.all([
        searchInCollection(productsRef, 'products'),
        searchInCollection(watchesRef, 'watches'),
        searchInCollection(jewelryRef, 'jewelry')
      ]);
      
      // Połącz wszystkie wyniki
      allResults.push(...productsResults, ...watchesResults, ...jewelryResults);
      
      console.log('Finalne wyniki po filtracji:', allResults.length);
      setProductSearchResults(allResults);
    } catch (err) {
      console.error('Błąd podczas wyszukiwania produktów:', err);
      enqueueSnackbar('Nie udało się wyszukać produktów.', { variant: 'error' });
    } finally {
      setProductSearchLoading(false);
    }
  };
  
  // Użycie funkcji debounce
  const searchTimeoutRef = useRef(null);
  
  // Funkcja obsługująca wybór produktu z wyników wyszukiwania
  const handleSelectProduct = (product) => {
    console.log('Wybrany produkt:', product); // Log dla debugowania
    
    // Ustal nazwę produktu bazując na dostępnych polach
    const productName = product.name || 
                        product.jewelry_name || 
                        (product.brand && product.model ? `${product.brand} ${product.model}` : 'Produkt bez nazwy');
    
    setFormData(prev => ({
      ...prev, 
      product_id: product.id,
      product_name: productName,
      product_sku: product.sku || '',
      product_collection: product._collectionType,
      product_image_url: product.images && product.images.length > 0 ? product.images[0] : null,
      current_location: product.location || ''
    }));
    setSelectedProduct(product);
    setOpenProductSearchDialog(false);
    setProductSearchTerm('');
    setProductSearchResults([]);
  };

  const debouncedSearchProduct = useCallback((searchTerm) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      handleProductSearch(searchTerm);
    }, 500); // 500ms debounce

  // Koniec funkcji debouncedSearchProduct
  }, [handleProductSearch]); // <-- dodany brakujący nawias i zależności useCallback

  // Główny render komponentu
  return (
    <>
      {/* Główny kontener z listą transferów */}
      <Container maxWidth="xl" sx={{ mt: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" component="h1" fontWeight="bold">
            Transfery produktów
          </Typography>
          
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleOpenNewTransferDialog}
          >
            Nowy transfer
          </Button>
        </Box>

        <Paper sx={{ mb: 3 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label={isAdmin ? "Wszystkie transfery" : "Moje transfery"} />
            <Tab label="Wysłane" />
            <Tab label="Otrzymane" />
          </Tabs>
          
          <Box sx={{ p: 2 }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : error ? (
              <Typography color="error" sx={{ p: 2 }}>{error}</Typography>
            ) : filteredTransfers.length === 0 ? (
              <Typography variant="body1" sx={{ p: 2, textAlign: 'center' }}>
                Brak transferów do wyświetlenia.
              </Typography>
            ) : (
              <Box sx={{ overflowX: 'auto' }}>
                <Box sx={{ minWidth: 900, width: '100%' }}>
                  <Grid container spacing={0} sx={{ 
                    borderBottom: '1px solid rgba(81, 81, 81, 0.3)', 
                    py: 1.5,
                    px: 2,
                    fontWeight: 'bold',
                    backgroundColor: theme.palette.primary.main,
                    color: '#fff',
                    borderRadius: '4px 4px 0 0'
                  }}>
                    <Grid item xs={2}>Data</Grid>
                    <Grid item xs={2}>Produkt</Grid>
                    <Grid item xs={2}>Z lokalizacji</Grid>
                    <Grid item xs={2}>Do lokalizacji</Grid>
                    <Grid item xs={1}>Metoda</Grid>
                    <Grid item xs={1}>Status</Grid>
                    <Grid item xs={2}>Akcje</Grid>
                  </Grid>
                  
                  {filteredTransfers.map((transfer) => {
                    const transferDate = transfer.created_at instanceof Timestamp 
                      ? transfer.created_at.toDate() 
                      : new Date(transfer.created_at);
                    
                    // Sprawdź czy transfer został lokalnie potwierdzony
                    const localStatus = transfer.localConfirmed ? 'locally_confirmed' : transfer.status;
                    const statusConfig = TRANSFER_STATUS_CONFIG[localStatus] || 
                      { label: 'Nieokreślony', color: 'default' };
                                        return (
                      <Grid 
                        container 
                        spacing={0} 
                        sx={{ 
                          borderBottom: '1px solid rgba(81, 81, 81, 0.15)',
                          py: 1.5,
                          px: 2,
                          '&:hover': { 
                            backgroundColor: 'rgba(212, 175, 55, 0.05)',
                            boxShadow: '0 2px 5px rgba(0, 0, 0, 0.08)'
                          },
                          transition: 'all 0.2s ease',
                          bgcolor: transfer.status === 'completed' ? 'rgba(76, 175, 80, 0.05)' : 
                                  transfer.status === 'cancelled' ? 'rgba(244, 67, 54, 0.05)' : 
                                  transfer.status === 'pending' ? 'rgba(255, 152, 0, 0.05)' : 'transparent'
                        }} 
                        key={transfer.id}
                        alignItems="center"
                      >
                        <Grid item xs={2}>
                          <Typography variant="body2">
                            {format(transferDate, 'dd.MM.yyyy, HH:mm', { locale: pl })}
                          </Typography>
                        </Grid>
                        
                        <Grid item xs={2}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {transfer.product_image_url && (
                              <Box 
                                sx={{ 
                                  width: 40, 
                                  height: 40, 
                                  mr: 1, 
                                  backgroundColor: '#000', 
                                  display: 'flex',
                                  justifyContent: 'center',
                                  alignItems: 'center',
                                  borderRadius: 1,
                                  overflow: 'hidden'
                                }}
                                onClick={() => handleOpenImagePreview(transfer.product_image_url)}
                              >
                                <img 
                                  src={transfer.product_image_url} 
                                  alt="" 
                                  style={{ 
                                    maxWidth: '100%',
                                    maxHeight: '100%', 
                                    objectFit: 'contain',
                                    cursor: 'pointer'
                                  }} 
                                />
                              </Box>
                            )}
                            <Box>
                              <Typography variant="body2" noWrap>
                                {transfer.product_name}
                              </Typography>
                              {transfer.product_sku && (
                                <Typography variant="caption" color="textSecondary">
                                  ID: {transfer.product_sku}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </Grid>
                        
                        <Grid item xs={2}>
                          <Tooltip title={LOCATIONS.find(l => l.shortName === transfer.origin_location)?.name || transfer.origin_location}>
                            <Typography variant="body2">
                              {LOCATIONS.find(l => l.shortName === transfer.origin_location)?.name || transfer.origin_location}
                            </Typography>
                          </Tooltip>
                        </Grid>
                        
                        <Grid item xs={2}>
                          <Tooltip title={LOCATIONS.find(l => l.shortName === transfer.destination_location)?.name || transfer.destination_location}>
                            <Typography variant="body2">
                              {LOCATIONS.find(l => l.shortName === transfer.destination_location)?.name || transfer.destination_location}
                            </Typography>
                          </Tooltip>
                        </Grid>
                        
                        <Grid item xs={1}>
                          <Tooltip title={SHIPPING_METHODS.find(m => m.value === mapLegacyShippingMethod(transfer.shipping_method))?.label || transfer.shipping_method}>
                            <Typography variant="body2" noWrap>
                              {SHIPPING_METHODS.find(m => m.value === mapLegacyShippingMethod(transfer.shipping_method))?.label || transfer.shipping_method}
                            </Typography>
                          </Tooltip>
                        </Grid>
                        
                        <Grid item xs={1}>
                          {/* Zakończone potwierdzenie */}
                          {transfer.status === 'completed' && (
                            <Chip
                              icon={<CheckCircleOutlineIcon />}
                              label="Zakończony"
                              color="success"
                              size="small"
                              variant="outlined"
                            />
                          )}
                          
                          {/* Anulowany */}
                          {transfer.status === 'cancelled' && (
                            <Chip
                              icon={<CancelIcon />}
                              label="Anulowany"
                              color="error"
                              size="small"
                              variant="outlined"
                            />
                          )}
                          
                          {/* W trakcie */}
                          {transfer.status === 'pending' && !transfer.processing && (
                            <Chip
                              icon={<HourglassEmptyIcon />}
                              label="W trakcie"
                              color="primary"
                              size="small"
                              variant="outlined"
                            />
                          )}
                          
                          {/* Przetwarzanie */}
                          {(transfer.status === 'processing' || transfer.processing) && (
                            <Chip
                              icon={<SyncIcon />}
                              label="Przetwarzanie"
                              color="secondary"
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </Grid>
                        
                        <Grid item xs={2}>
                          {isAdmin && (
                            <Button 
                              size="small" 
                              startIcon={<EditIcon />}
                              onClick={() => handleOpenEditTransferDialog(transfer)}
                              sx={{ mr: 1 }}
                            >
                              Edytuj
                            </Button>
                          )}
                          
                          {/* Przycisk Potwierdź odbiór widoczny tylko dla odbiorcy, gdy status = pending */}
                          {transfer.recipient_id === currentUser?.uid && transfer.status === 'pending' && (
                            <Button 
                              size="small" 
                              variant="contained"
                              color="success"
                              startIcon={<CheckCircleIcon />}
                              onClick={() => handleConfirmReceipt(transfer)}
                            >
                              Potwierdź odbiór
                            </Button>
                          )}
                        </Grid>
                      </Grid>
                    );
                  })}
                </Box>
              </Box>
            )}
          </Box>
        </Paper>
      </Container>

      {/* Dialogi */}
      <Dialog open={openTransferDialog} onClose={handleCloseTransferDialog} maxWidth="md" fullWidth>
  <DialogTitle sx={{ fontWeight: 'bold', borderBottom: '1px solid #eee' }}>
    {editingTransfer ? 'Edytuj Transfer Produktu' : 'Nowy Transfer Produktu'}
    <IconButton
      aria-label="close"
      onClick={handleCloseTransferDialog}
      sx={{
        position: 'absolute',
        right: 8,
        top: 8,
        color: (theme) => theme.palette.grey[500],
      }}
    >
      <CloseIcon />
    </IconButton>
  </DialogTitle>
  <DialogContent sx={{ pt: '20px !important' }}>
    <Grid container spacing={3}>
      <Grid item xs={12} sx={{ display: 'flex', alignItems: 'flex-end', gap: 2 }}>
        <TextField 
          label="Wybrany Produkt ID"
          value={formData.product_id || 'Nie wybrano'}
          fullWidth 
          InputProps={{ readOnly: true }}
          variant="filled"
        />
        <TextField 
          label="Nazwa Produktu"
          value={formData.product_name || 'Nie wybrano'}
          fullWidth 
          InputProps={{ readOnly: true }}
          variant="filled"
        />
        <Button 
          variant="outlined"
          color="primary"
          onClick={() => setOpenProductSearchDialog(true)} 
          startIcon={<SearchIcon />} 
          sx={{ 
            height: '56px', /* Dopasuj do wysokości TextField */
            minWidth: '120px', /* Minimalna szerokość przycisku */
            whiteSpace: 'nowrap' /* Zapobiega łamaniu tekstu */
          }}
        >
          Wyszukaj
        </Button>
      </Grid>

      {formData.product_id && (
        <Paper variant="outlined" sx={{p: 2, mt: 2}}>
          <Grid container spacing={2}>
            <Grid item xs={4} sm={2}>
              {formData.product_image_url ? (
                <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%'}}>
                  <Avatar 
                    src={formData.product_image_url}
                    alt={formData.product_name}
                    variant="rounded"
                    sx={{width: '100%', height: 'auto', aspectRatio: '1', border: `2px solid ${theme.palette.primary.main}`}}
                  />
                </Box>
              ) : (
                <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%'}}>
                  <Avatar 
                    variant="rounded"
                    sx={{width: '100%', height: 'auto', aspectRatio: '1', border: `2px solid ${theme.palette.primary.main}`}}
                  >
                    <ImageNotSupportedIcon />
                  </Avatar>
                </Box>
              )}
            </Grid>
            <Grid item xs={8} sm={10}>
              <Typography variant="h6">
                {formData.product_name}
              </Typography>
              
              {/* Marka i Model dla zegarków i niektórych produktów */}
              {formData.product_sku && (
                <Typography variant="body2" color="textSecondary">ID: {formData.product_sku}</Typography>
              )}
              
              {/* Lokalizacja produktu */}
              {formData.current_location && (
                <Typography variant="body2" color="textSecondary">
                  Aktualna lokalizacja: {LOCATIONS.find(l => l.shortName === formData.current_location)?.name || formData.current_location}
                </Typography>
              )}
            </Grid>
          </Grid>
        </Paper>
      )}
      
      {/* Pozostałe pola formularza transferu */}
      
      {/* Pole wyboru lokalizacji źródłowej */}
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth>
          <InputLabel id="origin-label">Źródło</InputLabel>
          <Select
            labelId="origin-label"
            name="origin_location"
            value={formData.origin_location}
            onChange={handleChange}
            required
          >
            {LOCATIONS
              .filter(loc => !loc.adminOnly || isAdmin) // Filtrujemy lokalizacje adminOnly dla zwykłych użytkowników
              .map(loc => (
                <MenuItem key={loc.shortName} value={loc.shortName}>
                  {loc.name}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      </Grid>
      
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth>
          <InputLabel id="recipient-label">Odbiorca</InputLabel>
          <Select
            labelId="recipient-label"
            name="recipient_id"
            value={formData.recipient_id}
            onChange={handleChange}
            required
          >
            {employees.map(emp => (
              <MenuItem key={emp.id} value={emp.id}>
                {emp.fullName || emp.email || emp.id}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth>
          <InputLabel id="destination-label">Cel</InputLabel>
          <Select
            labelId="destination-label"
            name="destination_location"
            value={formData.destination_location}
            onChange={handleChange}
            required
          >
            {LOCATIONS
              .filter(loc => !loc.adminOnly || isAdmin) // Filtrujemy lokalizacje adminOnly dla zwykłych użytkowników
              .map(loc => (
                <MenuItem key={loc.shortName} value={loc.shortName}>
                  {loc.name}
                </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth>
          <InputLabel id="shipping-method-label">Metoda wysyłki</InputLabel>
          <Select
            labelId="shipping-method-label"
            name="shipping_method"
            value={mapLegacyShippingMethod(formData.shipping_method)}
            onChange={handleChange}
            required
          >
            {SHIPPING_METHODS.map(method => (
              <MenuItem key={method.value} value={method.value}>
                {method.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      
      <Grid item xs={12} sm={6}>
        <Box>
          <TextField
            name="estimated_arrival"
            label="Szacowany czas dostawy"
            type="date"
            value={formData.estimated_arrival}
            onChange={handleChange}
            InputLabelProps={{ shrink: true }}
            fullWidth
            sx={{ mb: 1 }}
          />
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button 
              size="small" 
              variant="outlined" 
              onClick={() => handleQuickDateSelect(1)}
            >
              +1 dzień
            </Button>
            <Button 
              size="small" 
              variant="outlined" 
              onClick={() => handleQuickDateSelect(2)}
            >
              +2 dni
            </Button>
            <Button 
              size="small" 
              variant="outlined" 
              onClick={() => handleQuickDateSelect(3)}
            >
              +3 dni
            </Button>
            <Button 
              size="small" 
              variant="outlined" 
              onClick={() => handleQuickDateSelect(7)}
            >
              +7 dni
            </Button>
          </Box>
        </Box>
      </Grid>
      
      <Grid item xs={12}>
        <TextField
          name="notes"
          label="Uwagi"
          multiline
          rows={4}
          value={formData.notes}
          onChange={handleChange}
          fullWidth
        />
      </Grid>
      
      <Grid item xs={12}>
        <Typography variant="body2" color="textSecondary" sx={{ mt: 2, mb: 1 }}>
          Zdjęcie/dokument transferu (opcjonalnie)
        </Typography>
        <input
          accept="image/*"
          style={{ display: 'none' }}
          id="image-upload"
          type="file"
          onChange={handleImageChange}
        />
        <label htmlFor="image-upload">
          <Button
            variant="outlined"
            component="span"
            startIcon={<PhotoCameraIcon />}
          >
            Dodaj zdjęcie
          </Button>
        </label>
        {formData.image && (
          <Box sx={{ mt: 2 }}>
            <img 
              src={URL.createObjectURL(formData.image)} 
              alt="Podgląd" 
              style={{ maxWidth: '100%', maxHeight: '200px' }} 
            />
          </Box>
        )}
      </Grid>
    </Grid>
    </DialogContent>
    <DialogActions>
      <Button onClick={handleCloseTransferDialog} color="inherit">
        Anuluj
      </Button>
      <Button onClick={handleSaveTransfer} color="primary" variant="contained">
        {editingTransfer ? 'Zapisz zmiany' : 'Utwórz transfer'}
      </Button>
    </DialogActions>
  </Dialog>

  {/* Dialog wyszukiwania produktów */}
  <Dialog 
    open={openProductSearchDialog} 
    onClose={() => setOpenProductSearchDialog(false)}
    maxWidth="md" 
    fullWidth
  >
    <DialogTitle>
      Wyszukaj produkt
      <IconButton
        aria-label="close"
        onClick={() => setOpenProductSearchDialog(false)}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <CloseIcon />
      </IconButton>
    </DialogTitle>
    <DialogContent>
      <Box sx={{ mb: 2, mt: 1 }}>
        <TextField
          label="Wpisz nazwę, markę, model lub ID produktu..."
          value={productSearchTerm}
          onChange={(e) => {
            setProductSearchTerm(e.target.value);
            debouncedSearchProduct(e.target.value);
          }}
          fullWidth
          variant="outlined"
          InputProps={{
            startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />
          }}
        />
      </Box>
      
      {productSearchLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      )}
      
      {!productSearchLoading && productSearchResults.length === 0 && productSearchTerm.length > 1 && (
        <Box sx={{ textAlign: 'center', my: 4 }}>
          <Typography variant="body1" color="textSecondary">
            Nie znaleziono produktów pasujących do zapytania
          </Typography>
        </Box>
      )}
      
      {!productSearchLoading && productSearchResults.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" color="textSecondary">
            Znaleziono {productSearchResults.length} wyników
          </Typography>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {productSearchResults.map((product) => {
              const productName = product.name || 
                product.jewelry_name || 
                (product.brand && product.model ? `${product.brand} ${product.model}` : 'Produkt bez nazwy');
              
              return (
                <Grid item xs={12} sm={6} md={4} key={`${product._collectionType}-${product.id}`}>
                  <Card 
                    onClick={() => handleSelectProduct(product)}
                    sx={{ 
                      cursor: 'pointer',
                      transition: '0.2s',
                      '&:hover': { transform: 'translateY(-4px)', boxShadow: 3 }
                    }}
                  >
                    <CardMedia
                      component="div"
                      sx={{
                        height: 140,
                        backgroundColor: '#000',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        overflow: 'hidden'
                      }}
                    >
                      {product.images && product.images.length > 0 ? (
                        <img 
                          src={product.images[0]} 
                          alt={productName}
                          style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }}
                        />
                      ) : (
                        <ImageNotSupportedIcon sx={{ fontSize: 60, color: 'rgba(255,255,255,0.5)' }} />
                      )}
                    </CardMedia>
                    <CardContent>
                      <Typography variant="subtitle1" noWrap>
                        {productName}
                      </Typography>
                      
                      {(product.brand && product.brand !== productName && !productName.includes(product.brand)) && (
                        <Typography variant="body2" color="textSecondary">
                          {product.brand}
                        </Typography>
                      )}
                      
                      {(product.model && product.model !== productName && !productName.includes(product.model)) && (
                        <Typography variant="body2" color="textSecondary">
                          {product.model}
                        </Typography>
                      )}
                      
                      {product.sku && (
                        <Typography variant="body2" color="textSecondary">
                          ID: {product.sku}
                        </Typography>
                      )}
                      
                      {product.location && (
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                          <Typography variant="caption" color="textSecondary">
                            Lokalizacja: {LOCATIONS.find(l => l.shortName === product.location)?.name || product.location}
                          </Typography>
                        </Box>
                      )}
                      
                      <Chip 
                        size="small" 
                        label={product._collectionType === 'watches' ? 'Zegarek' : 
                               product._collectionType === 'jewelry' ? 'Biżuteria' : 'Produkt'} 
                        sx={{ mt: 1, fontSize: '0.7rem' }}
                        color={product._collectionType === 'watches' ? 'primary' : 
                               product._collectionType === 'jewelry' ? 'secondary' : 'default'}
                        variant="outlined"
                      />
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      )}
    </DialogContent>
  </Dialog>

  {/* Dialog podglądu obrazka */}
  <Dialog
    open={openImagePreviewDialog}
    onClose={() => setOpenImagePreviewDialog(false)}
    maxWidth="md"
  >
    <DialogContent sx={{ p: 1 }}>
      <img 
        src={previewImageUrl} 
        alt="Podgląd" 
        style={{ width: '100%', height: 'auto' }} 
      />
    </DialogContent>
    <DialogActions>
      <Button 
        onClick={() => setOpenImagePreviewDialog(false)} 
        color="primary"
      >
        Zamknij
      </Button>
    </DialogActions>
  </Dialog>
  </>
  );
};

export default ProductTransfers;
